import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BUSINESS_ADDRESS_MAX_LINES,
  BUSINESS_PHONE_PATTERN,
  BUSINESS_PROFILE_MAX_LENGTH,
  TAX_REGISTRATION_NUMBER_PATTERN,
  TENANT_LOGO_ACCEPTED_TYPES,
  type TenantBusinessProfileField,
  type TenantBusinessProfileFields,
} from '@optiflow/types';
import i18n from '@/i18n';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode, apiErrorMessage } from '@/services/apiClient';
import {
  useBusinessProfileQuery,
  useRemoveLogoMutation,
  useSaveBusinessProfileMutation,
  useUploadLogoMutation,
} from './businessProfile.api';

/** The upload limit the API enforces by default; checked here only to answer sooner. */
const LOGO_MAX_BYTES = 10_000_000;

type Draft = Record<TenantBusinessProfileField, string>;

const FIELDS: TenantBusinessProfileField[] = [
  'displayName',
  'legalName',
  'taxRegistrationNumber',
  'businessPhone',
  'businessAddress',
];

function toDraft(profile: TenantBusinessProfileFields): Draft {
  return Object.fromEntries(FIELDS.map((field) => [field, profile[field] ?? ''])) as Draft;
}

/** The client-side mirror of the server's rules, so a mistake shows before Save; the server stays authoritative. */
function fieldProblem(field: TenantBusinessProfileField, raw: string): string | null {
  const value = raw.trim();
  if (value === '') return null;
  if (value.length > BUSINESS_PROFILE_MAX_LENGTH[field]) return 'tooLong';
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (character === '<' || character === '>') return 'plainText';
    if (code === 10 && field === 'businessAddress') continue;
    if (code < 32 || code === 127) return 'plainText';
  }
  if (field === 'businessAddress' && value.split('\n').length > BUSINESS_ADDRESS_MAX_LINES)
    return 'tooManyLines';
  if (field === 'taxRegistrationNumber' && !TAX_REGISTRATION_NUMBER_PATTERN.test(value))
    return 'taxCharacters';
  if (field === 'businessPhone' && !BUSINESS_PHONE_PATTERN.test(value)) return 'phoneCharacters';
  return null;
}

/**
 * Settings › Company: who the business is.
 *
 * Branding (display name, logo), legal and fiscal identity (legal name, tax registration number) and
 * company-wide contact are separate sections because they are separate facts. The system name is
 * shown read-only: it is the tenant's identity and printed documents still use it. Nothing on this
 * page changes a printed document yet.
 */
export function CompanyPage() {
  const { t } = useTranslation();
  const profile = useBusinessProfileQuery();
  const save = useSaveBusinessProfileMutation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  // Re-read the fields only when the profile itself moved on: a logo change must not wipe unsaved edits.
  useEffect(() => {
    if (!profile.data || profile.data.version === draftVersion) return;
    setDraft(toDraft(profile.data));
    setDraftVersion(profile.data.version);
  }, [profile.data, draftVersion]);

  if (profile.isPending || !draft) return <LoadingState />;
  if (profile.isError) {
    return <Alert tone="error">{apiErrorMessage(profile.error) ?? t('company.loadError')}</Alert>;
  }

  const data = profile.data;
  const problems = Object.fromEntries(
    FIELDS.map((field) => [field, fieldProblem(field, draft[field])]),
  ) as Record<TenantBusinessProfileField, string | null>;
  const invalid = FIELDS.some((field) => problems[field] !== null);
  const unchanged = FIELDS.every((field) => draft[field].trim() === (data[field] ?? ''));

  const change = (field: TenantBusinessProfileField, value: string) => {
    setSaved(false);
    save.reset();
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  };

  const submit = () => {
    if (invalid || unchanged) return;
    const body = Object.fromEntries(
      FIELDS.map((field) => [field, draft[field].trim() === '' ? null : draft[field].trim()]),
    ) as unknown as TenantBusinessProfileFields;
    save.mutate({ ...body, version: data.version }, { onSuccess: () => setSaved(true) });
  };

  const field = (name: TenantBusinessProfileField, extra: { dir?: 'ltr' | 'auto' } = {}) => (
    <Input
      label={t(`company.fields.${name}`)}
      value={draft[name]}
      dir={extra.dir ?? 'auto'}
      maxLength={BUSINESS_PROFILE_MAX_LENGTH[name]}
      error={problems[name] ? t(`company.problems.${problems[name]}`) : undefined}
      hint={t(`company.hints.${name}`)}
      onChange={(event) => change(name, event.target.value)}
    />
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('company.title')} subtitle={t('company.subtitle')} />

      {saved ? <Alert tone="success">{t('company.saved')}</Alert> : null}
      {save.isError ? (
        <Alert tone="error">{errorText(save.error, t('company.saveError'))}</Alert>
      ) : null}

      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 xl:grid-cols-2">
        <Section
          title={t('company.sections.identity')}
          description={t('company.sections.identityHint')}
        >
          <div>
            <p className="field-label">{t('company.systemName')}</p>
            <p className="text-ink text-sm" dir="auto" data-testid="system-name">
              {data.systemName}
            </p>
            <p className="text-muted mt-1 text-xs">{t('company.systemNameHint')}</p>
          </div>
          {field('displayName')}
          <p className="text-muted text-xs" data-testid="resolved-name">
            {t('company.resolvedName')} <bdi>{draft.displayName.trim() || data.systemName}</bdi>
          </p>
        </Section>

        <Section
          title={t('company.sections.branding')}
          description={t('company.sections.brandingHint')}
        >
          <LogoEditor />
        </Section>

        <Section title={t('company.sections.tax')} description={t('company.sections.taxHint')}>
          {field('legalName')}
          {field('taxRegistrationNumber', { dir: 'ltr' })}
        </Section>

        <Section
          title={t('company.sections.contact')}
          description={t('company.sections.contactHint')}
        >
          {field('businessPhone', { dir: 'ltr' })}
          <AddressField
            value={draft.businessAddress}
            problem={problems.businessAddress}
            onChange={(value) => change('businessAddress', value)}
          />
        </Section>
      </div>

      <div className="flex justify-end">
        <Button onClick={submit} isLoading={save.isPending} disabled={invalid || unchanged}>
          {t('company.save')}
        </Button>
      </div>
    </div>
  );
}

function errorText(error: unknown, fallback: string): string {
  const code = apiErrorCode(error);
  return code && i18n.exists(`apiErrors.${code}`)
    ? i18n.t(`apiErrors.${code}`)
    : (apiErrorMessage(error) ?? fallback);
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader title={title} subtitle={description} />
      <div className="space-y-4 p-5">{children}</div>
    </Card>
  );
}

function AddressField({
  value,
  problem,
  onChange,
}: {
  value: string;
  problem: string | null;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {t('company.fields.businessAddress')}
      </label>
      <textarea
        id={id}
        dir="auto"
        rows={3}
        value={value}
        maxLength={BUSINESS_PROFILE_MAX_LENGTH.businessAddress}
        aria-invalid={problem ? true : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={
          problem
            ? 'w-full rounded-lg border border-red-400 bg-white px-3 py-2 text-sm'
            : 'border-line w-full rounded-lg border bg-white px-3 py-2 text-sm'
        }
      />
      <p className={problem ? 'mt-1 text-xs text-red-600' : 'text-muted mt-1 text-xs'}>
        {problem ? t(`company.problems.${problem}`) : t('company.hints.businessAddress')}
      </p>
    </div>
  );
}

/** Current logo, upload or replace, and remove. The server decodes and re-encodes the file; the browser only checks type and size to answer sooner. */
function LogoEditor() {
  const { t } = useTranslation();
  const profile = useBusinessProfileQuery();
  const upload = useUploadLogoMutation();
  const remove = useRemoveLogoMutation();
  const input = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const logo = profile.data?.logo ?? null;

  const choose = (file: File | undefined) => {
    if (!file) return;
    setLocalError(null);
    upload.reset();
    remove.reset();
    if (!(TENANT_LOGO_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
      setLocalError(t('company.logo.wrongType'));
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLocalError(t('company.logo.tooLarge'));
      return;
    }
    upload.mutate(file);
  };

  // The two refusals a person can act on get the page's own wording, never the server's English sentence.
  const uploadErrorText = (error: unknown) => {
    const code = apiErrorCode(error);
    if (code === 'MEDIA_TOO_LARGE') return t('company.logo.tooLarge');
    if (code === 'MEDIA_UNSUPPORTED_TYPE') return t('company.logo.wrongType');
    return errorText(error, t('company.logo.uploadError'));
  };

  const failure =
    localError ??
    (upload.isError ? uploadErrorText(upload.error) : null) ??
    (remove.isError ? errorText(remove.error, t('company.logo.removeError')) : null);

  return (
    <div className="space-y-3" data-testid="logo-editor">
      <div
        className="border-line bg-canvas flex h-32 items-center justify-center overflow-hidden rounded-lg border"
        data-testid="logo-preview"
      >
        {logo ? (
          <img
            src={logo.url}
            alt={t('company.logo.alt')}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <p className="text-muted text-sm">{t('company.logo.none')}</p>
        )}
      </div>
      <p className="text-muted text-xs">{t('company.logo.requirements')}</p>
      {failure ? <Alert tone="error">{failure}</Alert> : null}
      <input
        ref={input}
        type="file"
        className="sr-only"
        accept={TENANT_LOGO_ACCEPTED_TYPES.join(',')}
        data-testid="logo-input"
        aria-label={t('company.logo.choose')}
        onChange={(event) => {
          choose(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="secondary"
          isLoading={upload.isPending}
          disabled={remove.isPending}
          onClick={() => input.current?.click()}
        >
          {logo ? t('company.logo.replace') : t('company.logo.upload')}
        </Button>
        {logo ? (
          <Button
            variant="secondary"
            isLoading={remove.isPending}
            disabled={upload.isPending}
            onClick={() => {
              setLocalError(null);
              upload.reset();
              remove.mutate();
            }}
          >
            {t('company.logo.remove')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
